import { valibotResolver } from "@hookform/resolvers/valibot";
import { Link as LinkIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ProfileSearchParams } from "common/types";

import usePasteUrlTargeting from "../../../datahooks/usePasteUrlTargeting";
import { UrlTargetingSchema } from "../../../schemas";

export default function PasteUrlTargeting({
  accountId,
  setSearchParams,
}: {
  accountId: string;
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const [isOpen, setIsOpen] = useState(false);

  const { getSearchParamsFromUrl, isGettingSearchParamsFromUrl } =
    usePasteUrlTargeting();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      url: "",
    },
    resolver: valibotResolver(UrlTargetingSchema),
  });

  function onNext({ url }: { url: string }) {
    getSearchParamsFromUrl({ url, accountId }).then((result) => {
      setSearchParams(result);
      setIsOpen(false);
      reset();
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Trigger asChild>
        <Button size="sm" leftIcon={<LinkIcon />} variant="outline-black">
          Search by URL
        </Button>
      </Trigger>
      <Content>
        <Header title="Enter URL of audience from LinkedIn" />

        <Body>
          <FormField
            size={isDesktop ? "md" : "lg"}
            htmlFor="search-url"
            label="URL"
            hint="Applying URL over a set targeting will overwrite the settings. This cannot be undone"
            error={errors.url?.message}
          >
            <Input
              id="search-url"
              variant={isDesktop ? "md" : "lg"}
              placeholder="Enter URL here..."
              {...register("url")}
            />
          </FormField>
        </Body>

        <Footer className="justify-end">
          <Close asChild>
            <Button
              variant="secondary-black"
              onClick={() => setIsOpen(false)}
              disabled={isGettingSearchParamsFromUrl}
            >
              Cancel
            </Button>
          </Close>

          <Button
            onClick={handleSubmit(onNext)}
            isLoading={isGettingSearchParamsFromUrl}
          >
            Apply targeting
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
